import {
  type ElementType,
  type ReactNode,
  useEffect,
  useState,
  useContext,
  useMemo,
  memo,
} from "react";
import {
  Grid as GridBase,
  LinearProgress as LinearProgressBase,
} from "@mui/material";
import moment from "moment";
import isEmpty from "lodash/isEmpty";

import { renderUniversalTemplate } from "@/shared/helpers/reportHelper";
import { reportService } from "@/shared/services/reportService";
import useFetcher from "@/helpers/hooks/useFetcher";
import { GlobalContext } from "@/helpers/contexts/contexts";
import { MUICustomAutocompleteSelect } from "../muiCustom/select/MUICustomAutocompleteSelect";
import { BRAND_CONST, CUSTOM_WIDGET_PARAM } from "@/config/constants";
import {
  getFirstUniversalWidgetGroupId,
  getUniversalWidgetGroups,
  isDominosBrand,
} from "./universalWidgetTemplateUtils";
import RpCustomMonthPickerBase from "../datepicker/RpCustomMonthPicker";
// import useUserLocation from "@/helpers/hooks/useUserLocation";

type WidgetRecord = Record<string, any>;
type GlobalContextValue = {
  loggedInUser?: WidgetRecord;
  selectedBrand?: WidgetRecord;
};
type UniversalWidgetTemplateProps = {
  rows?: WidgetRecord[];
  renderChartWidget?: (..._args: any[]) => ReactNode;
  type?: string;
  requestParam?: WidgetRecord;
  embedWidgetParams?: WidgetRecord;
  hideDateSelector?: boolean;
};

const Grid = GridBase as ElementType;
const LinearProgress = LinearProgressBase as ElementType;
const RpCustomMonthPicker = RpCustomMonthPickerBase as ElementType;

export default memo(function UniversalWidgetTemplate({
  rows = [],
  renderChartWidget,
  type,
  requestParam,
  embedWidgetParams = {},
  hideDateSelector = false,
}: UniversalWidgetTemplateProps) {
  const { selectedBrand, loggedInUser } = useContext(
    GlobalContext
  ) as GlobalContextValue;

  const [month, setMonth] = useState<string | undefined>();
  const [groupsList, setGroupsList] = useState<WidgetRecord[]>([]);
  const [group, setGroup] = useState<string | number>("");
  const [location, setLocation] = useState("");
  const [campaignReport, setCampaignReport] = useState<WidgetRecord[] | null>(
    null
  );
  const [filter, setFilter] = useState<WidgetRecord>({});

  const shouldUseFirstGroupAsDefault = isDominosBrand(
    selectedBrand,
    BRAND_CONST.DOMINOS_BRAND_ID
  );
  const locationsList = useMemo(() => {
    const selectedBrandId = selectedBrand?.id;
    const locations = (loggedInUser?.partLocationData || []).filter(
      (loc: WidgetRecord) =>
        !selectedBrandId || String(loc?.fk_brand_id) === String(selectedBrandId)
    );

    // If no group is selected, return all locations
    if (!group) {
      return locations;
    }

    // Filter locations based on the selected group
    return locations.filter((location) => {
      // Handle cases where group_ids might be null or undefined
      if (!location.group_ids) return false;

      // Split the group_ids string into an array and check if it includes the selected group
      const locationGroupIds = location.group_ids
        .split(",")
        .map((id: string) => id.trim());
      return locationGroupIds.includes(String(group));
    });
  }, [loggedInUser, group, selectedBrand?.id]);

  // const { locations: locationsList, loading: isLoadingLocations } =
  //   useUserLocation({
  //     brandId: selectedBrand?.id,
  //   });

  // const firstActiveLoc = useMemo(() => {
  //   // Try to find a location that has both data and an active subscription
  //   const l1 = locationsList?.find(
  //     (l) => l.is_data_exists && l.is_current_subscription_active
  //   );

  //   // If no such location, try to find any location with an active subscription
  //   const l2 = locationsList?.find((l) => l.is_current_subscription_active);

  //   // If no such location, try to find one with data but no active subscription
  //   const l3 = locationsList?.find(
  //     (l) => l.is_data_exists && !l.is_current_subscription_active
  //   );

  //   // Return the first found location from the conditions above, or fallback to the first location in the list
  //   return l1 || l2 || l3 || locationsList?.[0] || "";
  // }, [locationsList]);

  useEffect(() => {
    setLocation("");
  }, [locationsList]);

  const reqData = useMemo(() => {
    const d: WidgetRecord = {
      dataType: type,
      id: selectedBrand?.id,
      ...filter,
      ...requestParam,
    };
    if (hideDateSelector) {
      // empty date if Report For is hidden
      d.date = "";
    }
    return d;
  }, [selectedBrand?.id, requestParam, filter, type, hideDateSelector]);

  const { data, isLoading: loading } = useFetcher(
    reqData,
    reportService.getUniversalWidgetReport,
    { revalidateOnFocus: false, fallback: rows }
  );

  useEffect(() => {
    const selectedBrandId = selectedBrand?.id;
    const groups = getUniversalWidgetGroups(data?.groupList, selectedBrandId);
    setGroupsList(groups);
  }, [data?.groupList, selectedBrand?.id]);

  useEffect(() => {
    if (!shouldUseFirstGroupAsDefault || group || groupsList.length === 0) {
      return;
    }

    const firstGroupId = getFirstUniversalWidgetGroupId(groupsList);
    if (firstGroupId) {
      setGroup(firstGroupId);
    }
  }, [group, groupsList, shouldUseFirstGroupAsDefault]);

  useEffect(() => {
    if (!month) {
      if (!data?.month_data) return;
      const d = moment.utc(data?.month_data);
      if (!d.isValid()) return;
      setMonth(moment(d).format("YYYY-MM"));
    }
  }, [data?.month_data, month]);

  useEffect(() => {
    if (!isEmpty(data?.data)) {
      setCampaignReport(data?.data);
    }
  }, [data?.data, loading]);

  const rowData =
    !loading && !isEmpty(data?.data) ? data?.data : campaignReport;

  useEffect(() => {
    const reqData: WidgetRecord = {};
    if (month && moment(month).isValid()) {
      reqData.date = moment(month).format("MM-DD-YYYY") || "";
    }
    if (group) {
      reqData.system_generated_group_id = group || "";
    }
    if (location) {
      reqData.system_generated_location_id = location || "";
    }
    setFilter(reqData);
  }, [month, location, group]);

  const embedWidgetParamsData = useMemo(() => {
    let params = {
      ...embedWidgetParams,
    };
    //the participant logs in the default for both filters is “All”, the embed URL only requires Participant ID
    if (type === "participant") {
      // params = {
      //   [CUSTOM_WIDGET_PARAM.ParticipantID]:
      //     loggedInUser?.participantData?.system_generated_participant_id,
      // };
      if (group) {
        params = {
          [CUSTOM_WIDGET_PARAM.ParticipantID]:
            loggedInUser?.participantData?.system_generated_participant_id,
          [CUSTOM_WIDGET_PARAM.GroupID]: group,
        };
      }
      // When the participant filters by a Location, the embed URL only requires Location ID
      if (location) {
        params = {
          [CUSTOM_WIDGET_PARAM.LocationID]: location,
        };
      }
    } else if (type === "brand") {
      if (group) {
        params = {
          ...params,
          [CUSTOM_WIDGET_PARAM.GroupID]: group,
        };
      }
    }
    return params;
  }, [
    type,
    location,
    group,
    embedWidgetParams,
    loggedInUser?.participantData?.system_generated_participant_id,
  ]);
  const filters = (labelColor = "var(--palette-text-primary)"): ReactNode => {
    return (
      <div className="universal-widget-filters mt-3">
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
        >
          {((type === "participant" &&
            BRAND_CONST.POPEYES_BRAND_ID !== selectedBrand?.id) ||
            type === "brand") && (
            <Grid
              item
              size={{
                xs: 12,
                sm: hideDateSelector ? 6 : true,
              }}
            >
              <MUICustomAutocompleteSelect
                options={groupsList.map((g: WidgetRecord) => ({
                  label: g.group_name,
                  value: g.system_generated_group_id,
                }))}
                selectProps={{
                  onChange: setGroup,
                  value: group,
                }}
                labelProps={{
                  style: {
                    color: labelColor,
                  },
                }}
                name="group"
                label="Groups"
                all={!shouldUseFirstGroupAsDefault}
              />
            </Grid>
          )}

          {type === "participant" && (
            <Grid
              item
              size={{
                xs: 12,
                sm: hideDateSelector ? 6 : true,
              }}
            >
              <MUICustomAutocompleteSelect
                options={locationsList.map((l: WidgetRecord) => ({
                  label: l.location_name,
                  value: l.system_generated_location_id,
                }))}
                selectProps={{
                  onChange: setLocation,
                  value: location,
                }}
                labelProps={{
                  style: {
                    color: labelColor,
                  },
                }}
                name="location"
                label="Location"
                all
              />
            </Grid>
          )}

          {!hideDateSelector ? (
            <Grid
              item
              size={{
                xs: 12,
                sm: type === "location" ? 6 : true,
              }}
            >
              <RpCustomMonthPicker
                onChange={setMonth}
                initialValue={month}
                label="Report For"
                labelProps={{
                  style: {
                    color: labelColor,
                  },
                }}
              />
            </Grid>
          ) : null}
        </Grid>
      </div>
    );
  };

  return (
    <div className={type === "participant" && rowData ? "mb-5" : ""}>
      <div>{loading && <LinearProgress />}</div>
      {/* <div>{(loading || isLoadingLocations) && <LinearProgress />}</div> */}
      {renderUniversalTemplate({
        rows: rowData,
        renderChartWidget,
        filters,
        embedWidgetParams: embedWidgetParamsData,
      })}
    </div>
  );
});
